import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import CapacitorStorage from './CapacitorStorage';
import { getCurrentAuthToken } from './Auth';
import type { AppNotification, NotificationLocalState } from 'Types/notifications';

const notificationsBaseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/notifications`;

const notificationStorageKey = (userId: string, notificationId: number) =>
  `notification:${userId}:${notificationId}`;

async function getAuthHeaders() {
  const token = await getCurrentAuthToken();
  if (!token) {
    return null;
  }

  return {
    Authorization: `Bearer ${token}`,
    'x-museflow-platform': Capacitor.getPlatform(),
  };
}

export async function fetchActiveAppNotification(): Promise<AppNotification | null> {
  const headers = await getAuthHeaders();
  if (!headers) {
    return null;
  }

  const response = await axios.get<AppNotification | null>(`${notificationsBaseUrl}/active`, {
    headers,
  });

  return response.data;
}

export async function recordNotificationImpression(notificationId: number): Promise<void> {
  const headers = await getAuthHeaders();
  if (!headers) return;

  await axios.post(`${notificationsBaseUrl}/${notificationId}/impression`, {}, { headers });
}

export async function recordNotificationDismiss(notificationId: number): Promise<void> {
  const headers = await getAuthHeaders();
  if (!headers) return;

  await axios.post(`${notificationsBaseUrl}/${notificationId}/dismiss`, {}, { headers });
}

export async function recordNotificationCtaClick(notificationId: number): Promise<void> {
  const headers = await getAuthHeaders();
  if (!headers) return;

  await axios.post(`${notificationsBaseUrl}/${notificationId}/cta-click`, {}, { headers });
}

export async function getLocalNotificationState(
  userId: string,
  notificationId: number,
): Promise<NotificationLocalState | null> {
  const raw = await CapacitorStorage.getItem(notificationStorageKey(userId, notificationId));
  if (!raw) return null;

  try {
    return JSON.parse(raw) as NotificationLocalState;
  } catch (error) {
    console.warn('Failed to parse local notification state:', error);
    await CapacitorStorage.removeItem(notificationStorageKey(userId, notificationId));
    return null;
  }
}

export async function saveLocalNotificationState(
  userId: string,
  notificationId: number,
  patch: Partial<NotificationLocalState>,
): Promise<NotificationLocalState> {
  const current = (await getLocalNotificationState(userId, notificationId)) || {};
  const next = {
    ...current,
    ...patch,
  };

  await CapacitorStorage.setItem(
    notificationStorageKey(userId, notificationId),
    JSON.stringify(next),
  );
  return next;
}
